import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import studentService from '../services/studentService';
import admissionService from '../services/admissionService';
import financeService from '../services/financeService';
import api from '../services/api';
import './Studenttenure.css';

// ── Fee engine — same config as the admission flow (RegistrationView) ──
const FOOD_PER_MONTH = 6000, MAINTENANCE = 5000;
const ACCOM_DEFAULTS = { '4sharing':10500, '3sharing':12500, '2sharing':14500, '2sharingPremium':19000, 'private':29000 };
const ROOM_TYPES = [
  ['4sharing','4 Sharing'], ['3sharing','3 Sharing'], ['2sharing','2 Sharing (Twin)'],
  ['2sharingPremium','2 Sharing (Premium)'], ['private','Single / Private'],
];
const ROOM_LABEL = Object.fromEntries(ROOM_TYPES);
const countMonths = (s, e) => {
  if (!s || !e) return null;
  const a = new Date(s), b = new Date(e);
  if (b <= a) return null;
  return Math.max((b.getFullYear()-a.getFullYear())*12 + (b.getMonth()-a.getMonth()) + 1, 1);
};

const inr = n => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtDate = d => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? '—' : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StudentTenure = ({ onNavigate, onLogout, studentId }) => {
  const [student, setStudent]     = useState(null);
  const [admission, setAdmission] = useState(null);
  const [feeDetail, setFeeDetail] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);   // 'renew' | 'complete' | 'cancel' | null
  const [busy, setBusy]           = useState(false);
  const [deduction, setDeduction] = useState(0);
  const [rates, setRates]         = useState({ rate4:0, rate3:0, rate2:0, rate2p:0, ratep:0 });
  // reallocation form (used by the Renew modal)
  const [rz, setRz] = useState({ type:'', from:'', till:'', depReq:0, card:0 });

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const s = await studentService.getStudentById(studentId);
      setStudent(s);
      const adm = await admissionService.getByStudentId(studentId).catch(() => null);
      if (adm) {
        setAdmission(adm);
        const fee = await financeService.getStudentFeeDetail(adm.id).catch(() => null);
        if (fee) setFeeDetail(fee);
      }
    } catch (e) {
      console.error('[StudentTenure] load error', e);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  // ── derived values ──
  const till       = admission?.validTill || admission?.validityDate;
  const daysLeft   = (() => {
    if (!till) return null;
    const d = Math.ceil((new Date(till) - new Date()) / 86400000);
    return d > 0 ? d : 0;
  })();
  const expiring   = daysLeft != null && daysLeft <= 30 && daysLeft >= 0;
  const statusRaw  = (admission?.status || '').toUpperCase();
  const isClosed   = statusRaw === 'CANCELLED' || statusRaw === 'COMPLETED';

  const num = v => Number(v || 0);
  const accom   = num(admission?.hostelFees);
  const food    = num(admission?.foodCharges);
  const maint   = num(admission?.maintenanceCharges);
  const deposit = num(admission?.refundableDeposit);
  const card    = num(admission?.prepaidCard);
  const total   = num(admission?.totalFees) || (accom + food + maint + deposit + card);
  const paid     = feeDetail?.paidAmount != null ? num(feeDetail.paidAmount) : num(admission?.paidFees);
  const balance  = Math.max(total - paid, 0);
  const cardBal  = feeDetail?.payCardBalance != null ? num(feeDetail.payCardBalance) : card;

  // ── room rates (for the reallocation modal) ──
  useEffect(() => {
    api.get('/room-rates')
      .then(r => { const d = r.data || {}; setRates({ rate4:d.rate4||0, rate3:d.rate3||0, rate2:d.rate2||0, rate2p:d.rate2p||0, ratep:d.ratep||0 }); })
      .catch(() => setRates({ rate4:0, rate3:0, rate2:0, rate2p:0, ratep:0 }));
  }, []);

  const accomRate = (rt) => {
    const m = { '4sharing':rates.rate4, '3sharing':rates.rate3, '2sharing':rates.rate2, '2sharingPremium':rates.rate2p, 'private':rates.ratep };
    return (m[rt] || 0) || ACCOM_DEFAULTS[rt] || 0;
  };

  const openRenew = () => {
    // refresh master rates so the renewal always prices at the current rate
    api.get('/room-rates')
      .then(r => { const d = r.data || {}; setRates({ rate4:d.rate4||0, rate3:d.rate3||0, rate2:d.rate2||0, rate2p:d.rate2p||0, ratep:d.ratep||0 }); })
      .catch(() => {});
    const start = till ? new Date(till) : new Date();
    start.setDate(start.getDate() + 1);                 // new term starts the day after current ends
    const end = new Date(start); end.setFullYear(end.getFullYear() + 1); end.setDate(end.getDate() - 1);
    setRz({
      type: admission?.roomType || '',
      from: start.toISOString().slice(0, 10),
      till: end.toISOString().slice(0, 10),
      depReq: deposit || 10000,
      card: 0,
    });
    setModal('renew');
  };

  // reallocation repricing
  const rzMonths  = countMonths(rz.from, rz.till);
  const rzRate    = accomRate(rz.type);
  const rzAccom   = rzRate * (rzMonths || 0);
  const rzFood    = FOOD_PER_MONTH * (rzMonths || 0);
  const rzDepDue  = Math.max(0, num(rz.depReq) - deposit);     // deposit already held carries forward
  const rzTotal   = rz.type && rzMonths ? rzAccom + rzFood + MAINTENANCE + rzDepDue + num(rz.card) : 0;
  const curMonthly = admission?.roomType ? accomRate(admission.roomType) + FOOD_PER_MONTH : 0;
  const newMonthly = rz.type ? rzRate + FOOD_PER_MONTH : 0;
  const dMonthly   = newMonthly - curMonthly;

  const doStatus = async (newStatus, note) => {
    if (!admission) return;
    setBusy(true);
    try {
      await admissionService.updateStatus(admission.id, newStatus, note || '');
      setModal(null);
      await load();
    } catch (e) {
      console.error('[StudentTenure] status update failed', e);
      alert('Could not update the admission. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const doRenew = async () => {
    if (!admission) return;
    if (!rz.from || !rz.till || !rzMonths) { alert('Please set a valid term (from / valid-till).'); return; }
    setBusy(true);
    try {
      await admissionService.renew(admission.id, {
        termFrom: rz.from,
        termTill: rz.till,
        roomType: rz.type || admission.roomType,
        monthsBilled: rzMonths,
        depositRequired: num(rz.depReq),
        payCardTopup: num(rz.card),
        operatorName: 'Renewal',
      });
      setModal(null);
      await load();
    } catch (e) {
      console.error('[StudentTenure] renew failed', e);
      alert('Could not complete the renewal. ' + (e?.response?.data?.message || 'Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="app" style={{ display: 'flex' }}>
        <Sidebar activeItem="admission" onNavigate={onNavigate} onLogout={onLogout} />
        <div className="tn-wrap"><div style={{ padding: 60, color: '#6B6B6B' }}>Loading tenure…</div></div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="app" style={{ display: 'flex' }}>
        <Sidebar activeItem="admission" onNavigate={onNavigate} onLogout={onLogout} />
        <div className="tn-wrap"><div style={{ padding: 60, color: '#6B6B6B' }}>Student not found.</div></div>
      </div>
    );
  }

  const initials = (student.fullName || 'NA').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="app" style={{ display: 'flex' }}>
      <Sidebar activeItem="admission" onNavigate={onNavigate} onLogout={onLogout} />

      <div className="tn-wrap">
        <div className="tn-top">
          <div className="tn-crumb">
            <a onClick={() => onNavigate('admission')}>Admission</a> /{' '}
            <a onClick={() => onNavigate('studentAnalytics', { studentId })}>Student Analytics</a> / <span>Tenure</span>
          </div>
          <h1 className="tn-title">Admission &amp; Tenure</h1>
        </div>

        {/* Student header */}
        <div className="tn-header">
          <div className="tn-photo">{initials}</div>
          <div className="tn-hinfo">
            <div className="tn-hname">{student.fullName || 'Student'}</div>
            <div className="tn-hmeta">
              <div className="tn-hmeta-item"><span>🆔</span> {admission?.admissionNumber || student.registrationId || 'N/A'}</div>
              <div className="tn-hmeta-item"><span>📍</span> {admission?.bedCode || 'Not allocated'}{admission?.roomType ? ` · ${admission.roomType}` : ''}</div>
              <div className="tn-hmeta-item"><span>📅</span> Admitted {fmtDate(admission?.dateOfAdmission)}</div>
            </div>
          </div>
          <div className="tn-hstatus">
            {isClosed
              ? <span className={`tn-pill ${statusRaw === 'CANCELLED' ? 'cancelled' : 'completed'}`}><span className="dot"></span> {statusRaw === 'CANCELLED' ? 'Cancelled' : 'Completed'}</span>
              : <span className="tn-pill active"><span className="dot"></span> Active</span>}
            {!isClosed && expiring && <span className="tn-pill expiring">⏳ Expiring in {daysLeft} days</span>}
          </div>
        </div>

        {/* Action bar */}
        <div className="tn-actions">
          <span className="lbl">Tenure actions</span>
          <button className="tn-btn tn-btn-primary" disabled={isClosed} onClick={openRenew}><span>↻</span> Renew admission</button>
          <button className="tn-btn tn-btn-secondary" disabled={isClosed} onClick={() => { setDeduction(0); setModal('complete'); }}><span>✓</span> Complete tenure</button>
          <button className="tn-btn tn-btn-danger" disabled={isClosed} onClick={() => { setDeduction(0); setModal('cancel'); }}><span>✕</span> Cancel admission</button>
          <button className="tn-btn tn-btn-ghost" onClick={() => window.print()}><span>⎙</span> Print statement</button>
        </div>

        {/* Expiry banner */}
        {!isClosed && expiring && (
          <div className="tn-banner">
            <span className="ic">⏳</span>
            <div className="txt"><strong>Current term ends {fmtDate(till)}.</strong> This admission will lapse on its valid-till date unless renewed.</div>
            <button className="tn-btn tn-btn-primary" onClick={openRenew}>Renew now</button>
          </div>
        )}

        {/* KPI strip */}
        <div className="tn-kpi">
          <div className="tn-kpi-card warn">
            <div className="tn-kpi-label">Current term</div>
            <div className="tn-kpi-value">{fmtDate(admission?.dateOfAdmission)}</div>
            <div className="tn-kpi-sub">→ {fmtDate(till)}</div>
          </div>
          <div className="tn-kpi-card warn">
            <div className="tn-kpi-label">Days remaining</div>
            <div className="tn-kpi-value">{daysLeft != null ? daysLeft : 'N/A'}</div>
            <div className="tn-kpi-sub">{till ? `valid till ${fmtDate(till)}` : 'validity not set'}</div>
          </div>
          <div className="tn-kpi-card green">
            <div className="tn-kpi-label">Security deposit held</div>
            <div className="tn-kpi-value">{inr(deposit)}</div>
            <div className="tn-kpi-sub">Refundable · carried across terms</div>
          </div>
          <div className="tn-kpi-card">
            <div className="tn-kpi-label">Pay-card balance</div>
            <div className="tn-kpi-value">{inr(cardBal)}</div>
            <div className="tn-kpi-sub">Closed-loop wallet</div>
          </div>
        </div>

        {/* Content */}
        <div className="tn-content">
          <div className="tn-grid">

            {/* Current term */}
            <div className="tn-card">
              <div className="tn-card-title">Current term {!isClosed && <span className="tn-pill active"><span className="dot"></span> Active</span>}</div>
              <div className="tn-card-sub">Billed as one continuous admission record.</div>

              <div className="tn-drow"><span className="k">Date of admission</span><span className="v">{fmtDate(admission?.dateOfAdmission)}</span></div>
              <div className="tn-drow"><span className="k">Valid till</span><span className="v">{fmtDate(till)}</span></div>
              <div className="tn-drow"><span className="k">Bed allocated</span><span className="v">{admission?.bedCode || '—'}</span></div>
              <div className="tn-drow"><span className="k">Room type</span><span className="v">{admission?.roomType || '—'}</span></div>
              <div className="tn-drow"><span className="k">Payment status</span>
                <span className="v" style={{ color: balance <= 0 ? '#2D5F4F' : '#E85D4A' }}>{balance <= 0 ? 'Paid in full' : `${inr(balance)} due`}</span></div>

              <div className="tn-seclabel">Fee breakdown — this term</div>
              <div className="tn-fee"><span className="k">Accommodation</span><span>{inr(accom)}</span></div>
              <div className="tn-fee"><span className="k">Food charges</span><span>{inr(food)}</span></div>
              <div className="tn-fee"><span className="k">Maintenance</span><span>{inr(maint)}</span></div>
              <div className="tn-fee"><span className="k">Security deposit (refundable)</span><span>{inr(deposit)}</span></div>
              {card > 0 && <div className="tn-fee"><span className="k">Pay-card load</span><span>{inr(card)}</span></div>}
              <div className="tn-fee total"><span>Total billed</span><span>{inr(total)}</span></div>
            </div>

            {/* Timeline */}
            <div className="tn-card">
              <div className="tn-card-title">Tenure timeline</div>
              <div className="tn-card-sub">Linked to one student ID — no re-registration on renewal.</div>
              <div className="tn-tl">
                <div className="tn-tlnode">
                  <span className="tn-tldot gold"></span>
                  <div className="tn-tlcard current">
                    <div className="tn-tlhead">
                      <span className="tn-tlterm">Current term</span>
                      {isClosed
                        ? <span className={`tn-pill ${statusRaw === 'CANCELLED' ? 'cancelled' : 'completed'}`}><span className="dot"></span>{statusRaw === 'CANCELLED' ? 'Cancelled' : 'Completed'}</span>
                        : expiring ? <span className="tn-pill expiring">Expiring · {daysLeft} days</span>
                        : <span className="tn-pill active"><span className="dot"></span> Active</span>}
                    </div>
                    <div className="tn-tldates">{fmtDate(admission?.dateOfAdmission)} → {fmtDate(till)}</div>
                    <div className="tn-tlmeta">{admission?.roomType || '—'} · <strong>{admission?.bedCode || '—'}</strong><br/>
                      {balance <= 0 ? 'Paid in full' : `${inr(balance)} due`} · pay-card balance {inr(cardBal)}</div>
                  </div>
                </div>
                {!isClosed && (
                  <div className="tn-tlnode">
                    <span className="tn-tldot ghost"></span>
                    <div className="tn-tlcard ghost">
                      <div className="tn-tlhead"><span className="tn-tlterm">Next term</span><span className="tn-pill pending"><span className="dot"></span> Awaiting decision</span></div>
                      <div className="tn-tldates">Proposed on renewal</div>
                      <div className="tn-tlmeta">Renew to continue, or let the term complete and close out the deposit.</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="tn-info-note">Past terms appear here once renewals are tracked (backend tenure-history — coming next).</div>
            </div>

          </div>
        </div>
      </div>

      {/* ===== MODALS ===== */}
      {modal === 'renew' && (
        <div className="tn-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="tn-modal">
            <div className="tn-mhead"><h3>Renew admission</h3><button className="tn-mclose" onClick={() => setModal(null)}>&times;</button></div>
            <div className="tn-mbody">
              <p className="tn-mintro">Creates a new term linked to <strong>{admission?.admissionNumber || 'this student'}</strong> — no re-registration. The security deposit carries forward and the bed is retained unless you change the room type.</p>

              <div className="tn-g2">
                <div className="tn-fg"><label className="tn-flabel">New term — from</label>
                  <input type="date" className="tn-finput" value={rz.from} onChange={e => setRz({ ...rz, from: e.target.value })} /></div>
                <div className="tn-fg"><label className="tn-flabel">Valid till</label>
                  <input type="date" className="tn-finput" value={rz.till} onChange={e => setRz({ ...rz, till: e.target.value })} /></div>
              </div>

              <div className="tn-fg">
                <label className="tn-flabel">Room / Bed</label>
                <select className="tn-finput" value={rz.type && rz.type !== admission?.roomType ? 'reallocate' : 'keep'}
                  onChange={e => { if (e.target.value === 'reallocate') setModal('reallocate'); else setRz({ ...rz, type: admission?.roomType || '' }); }}>
                  <option value="keep">Keep current bed — {ROOM_LABEL[admission?.roomType] || admission?.roomType || 'current room'}</option>
                  <option value="reallocate">Re-allocate a different bed…</option>
                </select>
                <p className="tn-fnote">{rz.type && rz.type !== admission?.roomType
                  ? `New room type: ${ROOM_LABEL[rz.type]} — re-priced below.`
                  : 'Changing room type re-prices accommodation and food for the new term.'}</p>
              </div>

              <div className="tn-fg">
                <label className="tn-flabel">Pay-card top-up (₹)</label>
                <input type="number" className="tn-finput" value={rz.card} onChange={e => setRz({ ...rz, card: Number(e.target.value) || 0 })} />
                <p className="tn-fnote">Existing {inr(cardBal)} balance carries over and is added to the top-up.</p>
              </div>

              <div className="tn-pay">
                <div className="tn-pay-label">New term — payable</div>
                <div className="tn-pay-row"><span className="k">Accommodation + food ({rzMonths || 0} mo)</span><span>{inr((accomRate(rz.type) + FOOD_PER_MONTH) * (rzMonths || 0))}</span></div>
                <div className="tn-pay-row"><span className="k">Maintenance (fixed)</span><span>{inr(MAINTENANCE)}</span></div>
                <div className="tn-pay-row"><span className="k">Security deposit</span><span>{rzDepDue > 0 ? inr(rzDepDue) : '₹0 — carried forward'}</span></div>
                <div className="tn-pay-row"><span className="k">Pay-card top-up</span><span>{inr(rz.card)}</span></div>
                <div className="tn-pay-total"><span>Total payable</span><span>{inr((accomRate(rz.type) + FOOD_PER_MONTH) * (rzMonths || 0) + MAINTENANCE + rzDepDue + num(rz.card))}</span></div>
              </div>
            </div>
            <div className="tn-mfoot">
              <button className="tn-btn tn-btn-secondary" onClick={() => setModal(null)}>Close</button>
              <button className="tn-btn tn-btn-primary" disabled={busy} onClick={doRenew}>{busy ? 'Saving…' : 'Confirm renewal'}</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'reallocate' && (
        <div className="tn-overlay" onClick={e => e.target === e.currentTarget && setModal('renew')}>
          <div className="tn-modal wide">
            <div className="tn-rz-head">
              <div>
                <h3>Room Reallocation</h3>
                <div className="sub">Renewal · {student.fullName} · {admission?.admissionNumber || ''} · {fmtDate(rz.from)} → {fmtDate(rz.till)}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span className="chip">Deposit {inr(deposit)} carried forward</span>
                <button className="close" onClick={() => setModal('renew')}>&times;</button>
              </div>
            </div>

            <div className="tn-rz">
              {/* LEFT — room type + term */}
              <div className="tn-rz-left">
                <div className="tn-rz-sec">Current allocation</div>
                <div className="tn-rz-cur">
                  <span className="rate">{inr(accomRate(admission?.roomType) + FOOD_PER_MONTH)} / mo</span>
                  <div className="type">{ROOM_LABEL[admission?.roomType] || admission?.roomType || '—'}</div>
                  <div className="loc">{admission?.bedCode || 'Bed not set'}</div>
                </div>

                <div className="tn-rz-typebar">
                  <label>New Room Type *</label>
                  <select value={rz.type} onChange={e => setRz({ ...rz, type: e.target.value })}>
                    <option value="">— Select Room Type —</option>
                    {ROOM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                {rz.type && (
                  <div className="tn-rz-strip">
                    <div className="tn-rz-chip"><span>Accommodation / mo: </span>{inr(rzRate)}</div>
                    <div className="tn-rz-chip"><span>Food / mo: </span>{inr(FOOD_PER_MONTH)}</div>
                    <div className="tn-rz-chip"><span>Total / mo: </span>{inr(rzRate + FOOD_PER_MONTH)}</div>
                  </div>
                )}

                <div className="tn-g2">
                  <div className="tn-fg"><label className="tn-flabel">Term from</label>
                    <input type="date" className="tn-finput" value={rz.from} onChange={e => setRz({ ...rz, from: e.target.value })} /></div>
                  <div className="tn-fg"><label className="tn-flabel">Valid till</label>
                    <input type="date" className="tn-finput" value={rz.till} onChange={e => setRz({ ...rz, till: e.target.value })} /></div>
                </div>
                <div className="tn-fg"><label className="tn-flabel">Months billed</label>
                  <input type="text" className="tn-finput" readOnly value={rzMonths ? rzMonths + ' months' : '—'} style={{ background: '#E8E4DF' }} />
                  <p className="tn-fnote">Admission month always billed in full.</p></div>
                <div className="tn-g2">
                  <div className="tn-fg"><label className="tn-flabel">Deposit required (₹)</label>
                    <input type="number" className="tn-finput" value={rz.depReq} onChange={e => setRz({ ...rz, depReq: Number(e.target.value) || 0 })} /></div>
                  <div className="tn-fg"><label className="tn-flabel">Prepaid card top-up (₹)</label>
                    <input type="number" className="tn-finput" value={rz.card} onChange={e => setRz({ ...rz, card: Number(e.target.value) || 0 })} /></div>
                </div>
              </div>

              {/* RIGHT — repricing */}
              <div className="tn-rz-right">
                <div className="tn-rz-sec">New term pricing</div>
                {!rz.type || !rzMonths ? (
                  <div className="tn-rz-ph">Select a room type and a valid term to reprice.</div>
                ) : (
                  <>
                    <div className="tn-rz-sum">
                      <div className="tn-srow"><span className="k">Room type</span><span style={{ fontWeight: 700, color: '#1B3B5F' }}>{ROOM_LABEL[rz.type]}</span></div>
                      <div className="tn-srow"><span className="k">Accommodation (× {rzMonths} mo)</span><span>{inr(rzAccom)}</span></div>
                      <div className="tn-srow"><span className="k">Food (× {rzMonths} mo)</span><span>{inr(rzFood)}</span></div>
                      <div className="tn-srow"><span className="k">Maintenance (fixed)</span><span>{inr(MAINTENANCE)}</span></div>
                      <div className="tn-srow"><span className="k">Security deposit required</span><span>{inr(rz.depReq)}</span></div>
                      <div className="tn-srow"><span className="k" style={{ paddingLeft: 12 }}>less: deposit already held</span><span style={{ color: '#2D5F4F' }}>– {inr(deposit)}</span></div>
                      <div className="tn-srow"><span className="k" style={{ paddingLeft: 12 }}>deposit due now</span><span>{inr(rzDepDue)}</span></div>
                      <div className="tn-srow"><span className="k">Prepaid card top-up</span><span>{inr(rz.card)}</span></div>
                      <div className="tn-srow refund" style={{ color: '#1B3B5F' }}><span>Total payable</span><span>{inr(rzTotal)}</span></div>
                    </div>
                    <div className={`tn-rz-cmp ${dMonthly > 0 ? 'up' : dMonthly < 0 ? 'down' : 'same'}`}>
                      {dMonthly === 0 ? (
                        <>
                          <div className="t">Same room type — no rate change</div>
                          <div>Lateral move within <strong>{ROOM_LABEL[rz.type]}</strong>. Only the bed changes; accommodation + food stay at {inr(newMonthly)}/mo.</div>
                        </>
                      ) : (
                        <>
                          <div className="t">Rate change vs current ({ROOM_LABEL[admission?.roomType] || '—'})</div>
                          <div><span className="d">{dMonthly > 0 ? '+' : '−'}{inr(Math.abs(dMonthly))}/mo</span> · <span className="d">{dMonthly > 0 ? '+' : '−'}{inr(Math.abs(dMonthly * rzMonths))} over {rzMonths} months</span> {dMonthly > 0 ? 'higher' : 'lower'} on accommodation + food.</div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="tn-rz-foot">
              <div className="note">Confirming records the room-type change and locks the repricing onto the renewal. The physical <strong>vacate &amp; reassign of a bed</strong> is carried out in the <strong>Rooms module</strong> once the renewal is confirmed.</div>
              <button className="tn-rzbtn tn-rzbtn-ghost" onClick={() => setRz({ ...rz, type: '' })}>Reset</button>
              <button className="tn-rzbtn tn-rzbtn-gold" disabled={!rz.type || !rzMonths} onClick={() => setModal('renew')}>Confirm Reallocation</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'complete' && (
        <div className="tn-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="tn-modal">
            <div className="tn-mhead"><h3>Complete tenure</h3><button className="tn-mclose" onClick={() => setModal(null)}>&times;</button></div>
            <div className="tn-mbody">
              <p className="tn-mintro">Closes the admission cleanly. The bed should be released to the pool and the deposit refunded after settling any dues.</p>
              <div className="tn-fg"><label className="tn-flabel">Damage / recovery deductions (₹)</label>
                <input type="number" className="tn-finput" value={deduction} onChange={e => setDeduction(Number(e.target.value) || 0)} /></div>
              <div className="tn-settle">
                <div className="tn-settle-title">Exit settlement preview</div>
                <div className="tn-srow"><span className="k">Security deposit held</span><span>{inr(deposit)}</span></div>
                <div className="tn-srow"><span className="k">Less: damage recovery</span><span className="minus">– {inr(deduction)}</span></div>
                <div className="tn-srow"><span className="k">Pay-card balance refund</span><span>{inr(cardBal)}</span></div>
                <div className="tn-srow refund"><span>Total refundable</span><span>{inr(Math.max(deposit - deduction, 0) + cardBal)}</span></div>
              </div>
              <div className="tn-info-note">Settlement figures are a preview — actual deposit refund / bed release processing is part of the backend step.</div>
            </div>
            <div className="tn-mfoot">
              <button className="tn-btn tn-btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="tn-btn tn-btn-primary" disabled={busy} onClick={() => doStatus('COMPLETED', `Tenure completed. Deductions ${inr(deduction)}.`)}>{busy ? 'Saving…' : 'Confirm completion'}</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'cancel' && (
        <div className="tn-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="tn-modal">
            <div className="tn-mhead"><h3>Cancel admission</h3><button className="tn-mclose" onClick={() => setModal(null)}>&times;</button></div>
            <div className="tn-mbody">
              <p className="tn-mintro">Ends the admission <strong>before</strong> the term completes. The bed should be released and the deposit settled per your early-exit policy.</p>
              <div className="tn-fg"><label className="tn-flabel">Damage / recovery deductions (₹)</label>
                <input type="number" className="tn-finput" value={deduction} onChange={e => setDeduction(Number(e.target.value) || 0)} /></div>
              <div className="tn-settle">
                <div className="tn-settle-title">Exit settlement preview</div>
                <div className="tn-srow"><span className="k">Security deposit held</span><span>{inr(deposit)}</span></div>
                <div className="tn-srow"><span className="k">Less: damage recovery</span><span className="minus">– {inr(deduction)}</span></div>
                <div className="tn-srow"><span className="k">Pay-card balance refund</span><span>{inr(cardBal)}</span></div>
                <div className="tn-srow refund"><span>Total refundable</span><span>{inr(Math.max(deposit - deduction, 0) + cardBal)}</span></div>
                <p className="tn-fnote" style={{ marginTop: 10 }}>Accommodation for unused months follows your refund / pro-rata rule.</p>
              </div>
              <div className="tn-info-note">Cancellation isn't live yet — it'll be enabled with the settlement &amp; bed-release step (coming next). This is a preview.</div>
            </div>
            <div className="tn-mfoot">
              <button className="tn-btn tn-btn-secondary" onClick={() => setModal(null)}>Keep admission</button>
              <button className="tn-btn tn-btn-danger" disabled title="Needs settlement / bed-release step">Confirm cancellation</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentTenure;